import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import CustomDataGrid from "../../../components/common/CustomDataGrid/CustomDataGrid";
import CustomDialog from "../../../components/common/CustomDialog";
import CustomSwitch from "../../../components/common/CustomSwitch";
import CustomRadioButton from "../../../components/common/CustomRadioButton/CustomRadioButton";
import CustomButton from "../../../components/common/CustomButton/CustomButton";
import { X } from "lucide-react";
import { GetSchedularConfig, DeleteSchedularConfig, UpdateSchedularConfig, AddSchedularConfig } from "../../../utils/urls";

const InvoiceSchedulerTab = () => {
  const [schedules, setSchedules] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, data: null });
  const [isPublishing, setIsPublishing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const validationSchema = Yup.object().shape({
    invoiceDate: Yup.string().required("Invoice generation date is required"),
    time: Yup.string().required("Time is required"),
    acceptanceWindow: Yup.string()
      .required("Acceptance window is required")
      .oneOf(Array.from({length: 10}, (_, i) => `${i+1} Day${i+1 > 1 ? "s" : ""}`), "Invalid acceptance window"),
  });

  const initialValues = {
    invoiceDate: "",
    time: "",
    acceptanceWindow: "",
    isActive: false,
  };

  const columns = [
    {
      field: "index",
      headerName: "#",
      width: "80px",
      renderCell: (row) => <span className="text-gray-600">{row.index + 1}</span>,
    },
    {
      field: "invoiceDate",
      headerName: "Invoice Date",
      width: "200px",
      renderCell: (row) => <span className="text-gray-700">{row.invoiceDate}</span>,
    },
    {
      field: "time",
      headerName: "Time",
      width: "150px",
      renderCell: (row) => <span className="text-gray-700">{row.time}</span>,
    },
    {
      field: "acceptanceWindow",
      headerName: "Acceptance Window",
      width: "150px",
      renderCell: (row) => <span className="text-gray-700">{row.acceptanceWindow}</span>,
    },
    {
      field: "actions",
      headerName: "Action",
      width: "200px",
      renderCell: (row) => (
        <div className="flex items-center gap-2">
          <CustomSwitch
            value={row.isActive}
            name={`schedule-status-${row.id}`}
            onChange={() => handleStatusChange(row.id)}
          />
          <button
            onClick={() => handleEdit(row)}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
            title="Edit"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
            </svg>
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="p-1.5 hover:bg-red-50 rounded-md transition-colors"
            title="Delete"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-red-600">
              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  // Fetch schedules on component mount
  useEffect(() => {
    const fetchSchedules = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(GetSchedularConfig);
        if (response.data.success) {
          const formattedSchedules = response.data.data.map((schedule, index) => ({
            id: schedule.id,
            invoiceDate: `${schedule.date}${schedule.date === 1 ? "st" : schedule.date === 2 ? "nd" : schedule.date === 3 ? "rd" : "th"} of Every Month`,
            time: formatTime(schedule.time),
            acceptanceWindow: `${schedule.acceptance_time_period} Day${schedule.acceptance_time_period > 1 ? "s" : ""}`,
            isActive: schedule.is_active,
            index: index,
          }));
          setSchedules(formattedSchedules);
        } else {
          console.error("Failed to fetch schedules:", response.data.message);
        }
      } catch (error) {
        console.error("Error fetching schedules:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSchedules();
  }, []);

  const formatTime = (time) => {
    const [hour, minute] = time.split(":");
    const hourInt = parseInt(hour);
    const period = hourInt >= 12 ? "PM" : "AM";
    const formattedHour = hourInt % 12 || 12;
    return `${formattedHour}:${minute} ${period}`;
  };

  const parseTimeForAPI = (time) => {
    const [hourMinute, period] = time.split(" ");
    let [hour, minute] = hourMinute.split(":");
    hour = parseInt(hour);
    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, "0")}:${minute}:00`;
  };

  const parseDateForAPI = (invoiceDate) => {
    const match = invoiceDate.match(/^(\d+)/);
    return match ? parseInt(match[1]) : 1;
  };

  const parseAcceptanceWindowForAPI = (acceptanceWindow) => {
    const match = acceptanceWindow.match(/^(\d+)/);
    return match ? parseInt(match[1]) : 1;
  };

  const handleStatusChange = (id) => {
    const schedule = schedules.find(s => s.id === id);
    setConfirmDialog({ 
      open: true, 
      type: 'status', 
      data: { id, newStatus: !schedule.isActive } 
    });
  };

  const handleEdit = (schedule) => {
    setEditData(schedule);
    setIsFormOpen(true);
  };

  const handleDelete = (id) => {
    setConfirmDialog({ 
      open: true, 
      type: 'delete', 
      data: { id } 
    });
  };

  const handleConfirmAction = async () => {
    setIsLoading(true);
    try {
      if (confirmDialog.type === 'delete') {
        const { id } = confirmDialog.data;
        const response = await axios.delete(DeleteSchedularConfig, {
          data: { id },
        });
        if (response.data.success) {
          setSchedules(prev => prev.filter(s => s.id !== id));
        } else {
          console.error("Failed to delete schedule:", response.data.message);
        }
      } else if (confirmDialog.type === 'status') {
        const { id, newStatus } = confirmDialog.data;
        const schedule = schedules.find(s => s.id === id);
        const payload = {
          id,
          date: parseDateForAPI(schedule.invoiceDate),
          time: parseTimeForAPI(schedule.time),
          acceptance_time_period: parseAcceptanceWindowForAPI(schedule.acceptanceWindow),
          is_active: newStatus,
        };
        const response = await axios.put(UpdateSchedularConfig, payload);
        if (response.data.success) {
          setSchedules(prev =>
            prev.map(s =>
              s.id === id ? { ...s, isActive: newStatus } : s
            )
          );
        } else {
          console.error("Failed to update status:", response.data.message);
        }
      }
    } catch (error) {
      console.error("Error during confirm action:", error);
    } finally {
      setIsLoading(false);
      setConfirmDialog({ open: false, type: null, data: null });
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    setIsLoading(true);
    try {
      if (editData) {
        // Update existing schedule
        const payload = {
          id: editData.id,
          date: parseDateForAPI(values.invoiceDate),
          time: parseTimeForAPI(values.time),
          acceptance_time_period: parseAcceptanceWindowForAPI(values.acceptanceWindow),
          is_active: values.isActive,
        };
        const response = await axios.put(UpdateSchedularConfig, payload);
        if (response.data.success) {
          setSchedules(prev =>
            prev.map(schedule =>
              schedule.id === editData.id
                ? { ...schedule, ...values }
                : schedule
            )
          );
        } else {
          console.error("Failed to update schedule:", response.data.message);
        }
      } else {
        // Create new schedule
        const payload = {
          date: parseDateForAPI(values.invoiceDate),
          time: parseTimeForAPI(values.time),
          acceptance_time_period: parseAcceptanceWindowForAPI(values.acceptanceWindow),
        };
        const response = await axios.post(AddSchedularConfig, payload);
        if (response.data.success) {
          const newSchedule = {
            id: response.data.data?.id || Date.now().toString(),
            invoiceDate: values.invoiceDate,
            time: values.time,
            acceptanceWindow: values.acceptanceWindow,
            isActive: isPublishing,
            index: schedules.length // Set correct index for new schedule
          };
          setSchedules(prev => [...prev, newSchedule]);
        } else {
          console.error("Failed to create schedule:", response.data.message);
        }
        
      }
    } catch (error) {
      console.error("Error during submit:", error);
    } finally {
      setIsLoading(false);
      handleCloseForm();
      resetForm();
    }
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditData(null);
    setIsPublishing(false);
    setIsTimePickerOpen(false);
  };

  const daysOfMonth = Array.from({ length: 31 }, (_, i) => i + 1);
  const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, "0"));
  const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, "0"));
  const periods = ["AM", "PM"];
  const acceptanceWindows = Array.from({ length: 10 }, (_, i) => `${i+1} Day${i+1 > 1 ? "s" : ""}`);

  const handleDateSelect = (day, setFieldValue) => {
    const suffix = day === 1 ? "st" : day === 2 ? "nd" : day === 3 ? "rd" : "th";
    setFieldValue("invoiceDate", `${day}${suffix} of Every Month`);
    setIsDatePickerOpen(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <CustomButton
          onClick={() => {
            setEditData(null);
            setIsFormOpen(true);
          }}
          label="Add New Schedule"
          variant="contained"
          disabled={isLoading}
        />
      </div>

      {isLoading ? (
        <div className="text-center text-gray-600">Loading...</div>
      ) : schedules.length === 0 ? (
        <div className="text-center text-gray-600">No data</div>
      ) : (
        <CustomDataGrid
          rows={schedules}
          columns={columns}
          keyId="id"
          perPageSize={5}
          className="bg-white rounded-lg shadow"
          loading={isLoading}
        />
      )}

      <CustomDialog
        open={isFormOpen}
        onClose={handleCloseForm}
        title=""
        className="!p-0"
      >
        <div className="w-[700px] h-[430px]">
          <div className="flex justify-between items-center bg-[#0051AD] px-4 py-3 rounded-t-lg">
            <span className="text-sm font-medium text-white">
              {editData ? "Edit Invoice Schedule" : "Create New Invoice Schedule"}
            </span>
            <button 
              type="button"
              onClick={handleCloseForm}
              className="text-white hover:text-gray-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <Formik
            initialValues={editData || initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ errors, touched, setFieldValue, values }) => (
              <Form className="p-4">
                <div className="space-y-4">
                  <div className="mb-4 relative">
                    <label className="text-[#282828] font-medium mb-2 block">Select Invoice Generation Date</label>
                    <div 
                      className="w-full border rounded-md p-2 cursor-pointer"
                      onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                    >
                      {values.invoiceDate || "Select"}
                    </div>
                    {isDatePickerOpen && (
                      <div className="absolute z-10 mt-1 bg-white border rounded-md shadow-lg p-4">
                        <div className="grid grid-cols-7 gap-2">
                          {daysOfMonth.map((day) => (
                            <div
                              key={day}
                              className={`p-2 text-center rounded-full cursor-pointer ${
                                selectedDate === day ? "bg-blue-500 text-white" : "hover:bg-gray-100"
                              }`}
                              onClick={() => {
                                setSelectedDate(day);
                                handleDateSelect(day, setFieldValue);
                              }}
                            >
                              {day}
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between mt-4">
                          <span className="text-gray-600">{selectedDate || "1"} of Every Month</span>
                          <CustomButton
                            label="Select"
                            variant="contained"
                            onClick={() => setIsDatePickerOpen(false)}
                          />
                        </div>
                      </div>
                    )}
                    {errors.invoiceDate && touched.invoiceDate && (
                      <div className="text-red-500 text-sm mt-1">{errors.invoiceDate}</div>
                    )}
                  </div>

                  <div className="mb-4 relative">
                    <label className="text-[#282828] font-medium mb-2 block">Select Time</label>
                    <div 
                      className="w-full border rounded-md p-2 cursor-pointer"
                      onClick={() => setIsTimePickerOpen(!isTimePickerOpen)}
                    >
                      {values.time || "Select"}
                    </div>
                    {isTimePickerOpen && (
                      <div className="absolute z-20 mt-1 bg-white border rounded-md shadow-lg p-3 w-48">
                        <div className="flex justify-between gap-1 h-32">
                          <div className="flex-1 overflow-y-auto scrollbar-hidden">
                            {hours.map((hour) => {
                              const [currentHour] = values.time ? values.time.split(":") : ["01"];
                              return (
                                <div
                                  key={hour}
                                  className={`py-1 px-2 text-center text-sm cursor-pointer rounded ${
                                    hour === currentHour
                                      ? "bg-blue-500 text-white"
                                      : "hover:bg-gray-100"
                                  }`}
                                  onClick={() => {
                                    const [_, minute, period] = values.time ? values.time.split(/[: ]/) : ["", "00", "AM"];
                                    setFieldValue("time", `${hour}:${minute} ${period}`);
                                  }}
                                >
                                  {hour}
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex-1 overflow-y-auto scrollbar-hidden">
                            {minutes.map((minute) => {
                              const [, currentMinute] = values.time ? values.time.split(/[: ]/) : ["", "00"];
                              return (
                                <div
                                  key={minute}
                                  className={`py-1 px-2 text-center text-sm cursor-pointer rounded ${
                                    minute === currentMinute
                                      ? "bg-blue-500 text-white"
                                      : "hover:bg-gray-100"
                                  }`}
                                  onClick={() => {
                                    const [hour = "01", , period] = values.time ? values.time.split(/[: ]/) : ["01", "", "AM"];
                                    setFieldValue("time", `${hour}:${minute} ${period}`);
                                  }}
                                >
                                  {minute}
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex-1 overflow-y-auto scrollbar-hidden">
                            {periods.map((period) => {
                              const [, , currentPeriod] = values.time ? values.time.split(/[: ]/) : ["", "", "AM"];
                              return (
                                <div
                                  key={period}
                                  className={`py-1 px-2 text-center text-sm cursor-pointer rounded ${
                                    period === currentPeriod
                                      ? "bg-blue-500 text-white"
                                      : "hover:bg-gray-100"
                                  }`}
                                  onClick={() => {
                                    const [hour = "01", minute = "00"] = values.time ? values.time.split(/[: ]/) : ["01", "00"];
                                    setFieldValue("time", `${hour}:${minute} ${period}`);
                                  }}
                                >
                                  {period}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div className="flex justify-end mt-2">
                          <CustomButton
                            label="Select"
                            variant="contained"
                            onClick={() => setIsTimePickerOpen(false)}
                          />
                        </div>
                      </div>
                    )}
                    {errors.time && touched.time && (
                      <div className="text-red-500 text-sm mt-1">{errors.time}</div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="text-[#282828] font-medium mb-2 block">Invoice Acceptance Time Period</label>
                    <Field as="select" name="acceptanceWindow" className="w-full border rounded-md p-2">
                      <option value="">Select</option>
                      {acceptanceWindows.map((window) => (
                        <option key={window} value={window}>{window}</option>
                      ))}
                    </Field>
                    {errors.acceptanceWindow && touched.acceptanceWindow && (
                      <div className="text-red-500 text-sm mt-1">{errors.acceptanceWindow}</div>
                    )}
                  </div>

                  {editData && (
                    <div className="mb-4">
                      <label className="text-[#282828] font-medium mb-2 block">Status</label>
                      <CustomRadioButton
                        name="isActive"
                        value={values.isActive}
                        inRow={true}
                        radioList={[
                          { label: "Active", value: true },
                          { label: "Inactive", value: false },
                        ]}
                        onChange={(e) => setFieldValue("isActive", e.target.value === "true")}
                      />
                    </div>
                  )}

                  <div className="flex justify-end gap-3 mt-6">
                    <CustomButton
                      type="button"
                      onClick={handleCloseForm}
                      label="Cancel"
                      variant="outlined"
                      disabled={isLoading}
                    />
                    {/* {!editData && (
                      <CustomButton
                        type="submit"
                        onClick={() => setIsPublishing(false)}
                        label={isLoading ? "Saving..." : "Save"}
                        variant="blur"
                        disabled={isLoading}
                      />
                    )} */}
                    <CustomButton
                      type="submit"
                      onClick={() => setIsPublishing(true)}
                      label={isLoading ? "Saving..." : editData ? "Save" : "Publish"}
                      variant="contained"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </CustomDialog>

      <CustomDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: null, data: null })}
        title="Are You Sure?"
        titleClassName="text-center"
        className=""
      >
        <div className="w-[400px] text-center">
          <p className="text-gray-600 text-left border-b pb-4">
            {confirmDialog.type === 'delete' 
              ? "Are you sure you want to delete this schedule?"
              : "Are you sure you want to change the status of this schedule?"}
          </p>
          <div className="flex justify-between gap-3 mt-4">
            <button
              className="py-2 px-4 bg-gray-200 font-semibold text-base w-[170px] rounded-md"
              onClick={() => setConfirmDialog({ open: false, type: null, data: null })}
            >
              Cancel
            </button>
            <button
              className="py-2 px-4 bg-blue-500 w-[170px] font-semibold text-base text-white rounded-md disabled:opacity-70"
              onClick={handleConfirmAction}
              disabled={isLoading}
            >
              {isLoading ? "Processing..." : "Confirm"}
            </button>
          </div>
        </div>
      </CustomDialog>
    </div>
  );
};

export default InvoiceSchedulerTab;